import { clampOffset, isZeroOffset, OFFSET_STEP, offsetText } from "../../src/lib/offset.ts";
import type { Offset } from "../../src/lib/offset.ts";
import { offsetWords } from "../../src/lib/placements.ts";
import type { Spot } from "../../src/lib/types.ts";
import { h } from "./dom.ts";

// Moving a logo within its spot on a preview photo, shared by the designer and the Studio: the math, the control under
// each width slider (positionControls, kept current by showPosition) and the drag (bindDrag). The overlay itself is
// placed by placeOverlay (preview.ts), which takes the offset.

export const ZERO_OFFSET: Offset = { offsetXIn: 0, offsetYIn: 0 };

export type Nudge = "up" | "down" | "left" | "right";
export const NUDGES: Nudge[] = ["up", "down", "left", "right"];
export const NUDGE_ARROW: Record<Nudge, string> = { up: "↑", down: "↓", left: "←", right: "→" };
export const NUDGE_LABEL: Record<Nudge, string> = {
  up: `Move up ${OFFSET_STEP} in`, down: `Move down ${OFFSET_STEP} in`, left: `Move left ${OFFSET_STEP} in`, right: `Move right ${OFFSET_STEP} in`,
};
const NUDGE_STEP: Record<Nudge, Offset> = {
  up: { offsetXIn: 0, offsetYIn: -OFFSET_STEP }, down: { offsetXIn: 0, offsetYIn: OFFSET_STEP },
  left: { offsetXIn: -OFFSET_STEP, offsetYIn: 0 }, right: { offsetXIn: OFFSET_STEP, offsetYIn: 0 },
};
const ARROW_KEYS: Record<string, Nudge> = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };

type Limits = { maxWidthIn: number; maxHeightIn: number };
type Size = { widthIn: number; heightIn: number };

export const sameOffset = (a: Offset, b: Offset) => a.offsetXIn === b.offsetXIn && a.offsetYIn === b.offsetYIn;

// The pages keep the position the buyer chose apart from the one shown: what shows (and what is sent) is the choice
// kept inside the room the spot has now, so widening the logo to fill the spot and narrowing it back brings the
// sideways move back. Zero while the spot cannot be placed.
export function shownOffset(limits: Limits | null, size: Size | null, chosen: Offset): Offset {
  return limits && size ? clampOffset(limits, size.widthIn, size.heightIn, chosen) : ZERO_OFFSET;
}

// The new choice after a drag or nudge to `next` (from the shown position), or null when the logo does not move on
// the photo. Only the way it moved is taken: a nudge down while there is no sideways room keeps the sideways choice.
export function chooseOffset(limits: Limits | null, size: Size | null, chosen: Offset, next: Offset): Offset | null {
  if (!limits || !size) return null;
  const now = shownOffset(limits, size, chosen);
  const to = shownOffset(limits, size, next);
  if (sameOffset(now, to)) return null;
  return {
    offsetXIn: to.offsetXIn === now.offsetXIn ? chosen.offsetXIn : to.offsetXIn,
    offsetYIn: to.offsetYIn === now.offsetYIn ? chosen.offsetYIn : to.offsetYIn,
  };
}

// Screen pixels per printed inch on the photo as shown: the photo is scaled from its natural width to its displayed
// width, and the garment's ppi is per natural pixel. 0 while the photo has no size yet.
export function displayedPxPerIn(displayedWidthPx: number, naturalWidthPx: number, ppi: number): number {
  return displayedWidthPx > 0 && naturalWidthPx > 0 && ppi > 0 ? (displayedWidthPx / naturalWidthPx) * ppi : 0;
}

// The offset after dragging dx, dy screen pixels from where the drag started: snapped to the quarter-inch grid and
// kept inside the spot.
export function dragOffset(limits: Limits, size: Size, start: Offset, dxPx: number, dyPx: number, pxPerIn: number): Offset {
  if (!(pxPerIn > 0)) return clampOffset(limits, size.widthIn, size.heightIn, start);
  return clampOffset(limits, size.widthIn, size.heightIn, { offsetXIn: start.offsetXIn + dxPx / pxPerIn, offsetYIn: start.offsetYIn + dyPx / pxPerIn });
}

export function nudgeOffset(limits: Limits, size: Size, o: Offset, dir: Nudge): Offset {
  const d = NUDGE_STEP[dir];
  return clampOffset(limits, size.widthIn, size.heightIn, { offsetXIn: o.offsetXIn + d.offsetXIn, offsetYIn: o.offsetYIn + d.offsetYIn });
}

// Which nudge buttons do nothing from here (the logo is at that edge of the spot).
export function nudgeDisabled(limits: Limits, size: Size, o: Offset): Record<Nudge, boolean> {
  const from = clampOffset(limits, size.widthIn, size.heightIn, o);
  const out = {} as Record<Nudge, boolean>;
  for (const dir of NUDGES) out[dir] = sameOffset(nudgeOffset(limits, size, from, dir), from);
  return out;
}

export function arrowNudge(key: string): Nudge | null {
  return ARROW_KEYS[key] ?? null;
}

// What a screen reader hears on the focused overlay.
export function overlayLabel(spotLabel: string, o: Offset): string {
  return `${spotLabel} logo, ${offsetText(o)}. Drag it or use the arrow keys to move it.`;
}

// One line per placement for the Sent summary, in the share page's words: "Front center: 2 in lower, 1 in left of
// standard as you face the shirt", or "Front center: Standard position".
export function positionLine(spotLabel: string, o: Offset): string {
  return `${spotLabel}: ${offsetWords(o)}`;
}

export interface DragHandlers {
  // Called on pointer down; false refuses the drag (the page is busy, or nothing is placed yet).
  begin(): boolean;
  // The pointer has moved this far, in screen pixels, from where it went down.
  move(dxPx: number, dyPx: number): void;
  end(): void;
}

type DragTarget = Pick<HTMLElement, "addEventListener" | "setPointerCapture" | "releasePointerCapture" | "hasPointerCapture">;

// Pointer dragging on one element. The pointer is captured, so the drag keeps going when it leaves the logo.
export function bindDrag(el: DragTarget, h: DragHandlers): void {
  let drag: { id: number; x: number; y: number } | null = null;
  el.addEventListener("pointerdown", (e: PointerEvent) => {
    if (drag || (e.pointerType === "mouse" && e.button !== 0) || !h.begin()) return;
    e.preventDefault();
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY };
    el.setPointerCapture(e.pointerId);
  });
  el.addEventListener("pointermove", (e: PointerEvent) => {
    if (drag && e.pointerId === drag.id) h.move(e.clientX - drag.x, e.clientY - drag.y);
  });
  const stop = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    drag = null;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    h.end();
  };
  el.addEventListener("pointerup", stop);
  el.addEventListener("pointercancel", stop);
}

// The control under a placement's width slider, the same on the designer and the Studio: four quarter-inch nudges, a
// way back to the standard position, and the position in words. showPosition keeps it current without a rebuild, so
// the button in use keeps focus.
export function positionControls(spot: Spot, spotLabel: string, o: Offset, locked: boolean, on: { nudge(dir: Nudge): void; reset(): void }): HTMLElement {
  return h("div", { class: "position" },
    h("div", { class: "nudge", role: "group", "aria-label": `Move the ${spotLabel.toLowerCase()} logo` },
      ...NUDGES.map((dir) => h("button", { id: `nudge-${spot}-${dir}`, type: "button", class: "chip", "aria-label": NUDGE_LABEL[dir], title: NUDGE_LABEL[dir],
        disabled: locked, onclick: () => on.nudge(dir) }, NUDGE_ARROW[dir])),
      h("button", { id: `reset-${spot}`, type: "button", class: "link", hidden: isZeroOffset(o), disabled: locked, onclick: () => on.reset() }, "Reset position")),
    h("p", { id: `pos-${spot}`, class: "muted", "aria-live": "polite" }, offsetText(o)));
}

// The logo on the preview photo, ready to drag: focusable, named by where it sits, and never the browser's own image
// drag (which would steal the pointer).
export function movableOverlay(spot: Spot, spotLabel: string, o: Offset, className: string, src: string): HTMLImageElement {
  const img = h("img", { id: `ov-${spot}`, class: `overlay movable ${className}`.trim(), src, alt: overlayLabel(spotLabel, o), tabIndex: 0 });
  img.draggable = false;
  return img;
}

type Doc = Pick<Document, "getElementById" | "activeElement">;

// The words, the nudges that still move the logo (stuck: those that would not; null while it cannot be placed),
// Reset (hidden at the standard position) and the overlay's name for one spot.
export function showPosition(doc: Doc, spot: Spot, spotLabel: string, o: Offset, stuck: Record<Nudge, boolean> | null, locked: boolean): void {
  for (const dir of NUDGES) {
    const button = doc.getElementById(`nudge-${spot}-${dir}`) as HTMLButtonElement | null;
    if (button) button.disabled = locked || !stuck || stuck[dir];
  }
  const reset = doc.getElementById(`reset-${spot}`) as HTMLButtonElement | null;
  if (reset) {
    reset.hidden = isZeroOffset(o);
    reset.disabled = locked;
  }
  const line = doc.getElementById(`pos-${spot}`);
  if (line) line.textContent = offsetText(o);
  const overlay = doc.getElementById(`ov-${spot}`) as HTMLImageElement | null;
  if (overlay) overlay.alt = overlayLabel(spotLabel, o);
}

const OPPOSITE: Record<Nudge, Nudge> = { up: "down", down: "up", left: "right", right: "left" };

// A nudge button that reaches the edge is disabled, so focus goes to the way back.
export function focusAfterNudge(doc: Doc, spot: Spot, dir: Nudge): void {
  const button = doc.getElementById(`nudge-${spot}-${dir}`) as HTMLButtonElement | null;
  if (button?.disabled && doc.activeElement === button) (doc.getElementById(`nudge-${spot}-${OPPOSITE[dir]}`) as HTMLElement | null)?.focus();
}

// Reset hides itself, so focus goes to the first nudge button that still works, else the logo.
export function focusAfterReset(doc: Doc, spot: Spot): void {
  const next = NUDGES.map((d) => doc.getElementById(`nudge-${spot}-${d}`) as HTMLButtonElement | null).find((b) => b && !b.disabled);
  ((next ?? doc.getElementById(`ov-${spot}`)) as HTMLElement | null)?.focus();
}
